import type {
  ConversationLabelResponse,
  ConversationMetadata,
  ReplayConversation,
  ReplayConversationResponse
} from './types';

const FETCH_TIMEOUT_MS = 15_000;

interface ApiErrorResponse {
  error?: string;
}

export class ConversationsApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ConversationsApiError';
    this.status = status;
  }
}

function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs = FETCH_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
  });
}

async function parseError(response: Response, fallback: string): Promise<ConversationsApiError> {
  const error = (await response.json().catch(() => ({}))) as ApiErrorResponse;
  return new ConversationsApiError(error.error || fallback, response.status);
}

export const getConversationMetadata = async (
  conversationId: string,
  accessToken: string
): Promise<ConversationMetadata> => {
  const response = await fetchWithTimeout(`/backend/conversations/${conversationId}`, {
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'Content-Type': 'application/json',
      'conversation-id': conversationId
    }
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to fetch conversation');
  }

  return (await response.json()) as ConversationMetadata;
};

export const getReplayConversation = async (
  conversationId: string
): Promise<ReplayConversation | null> => {
  const response = await fetchWithTimeout(
    `/backend/conversations/${conversationId}/messages?include=conversation`,
    { cache: 'no-store' }
  );
  if (!response.ok) return null;

  const data = (await response.json()) as ReplayConversationResponse;
  return { ...data.conversation, messages: data.messages };
};

export const patchConversationName = async (
  conversationId: string,
  name: string | null,
  accessToken: string
): Promise<void> => {
  const response = await fetchWithTimeout('/backend/conversations', {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'conversation-id': conversationId
    },
    body: JSON.stringify({ name })
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to rename conversation');
  }
};

export const generateConversationLabel = async (
  conversationId: string,
  accessToken: string
): Promise<ConversationLabelResponse> => {
  const response = await fetchWithTimeout(`/backend/conversations/${conversationId}/label`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to label conversation');
  }

  return (await response.json()) as ConversationLabelResponse;
};
